/* ==========================================================================
   ZYREX — assets/product.js
   The four custom elements that make a product page work.

     <variant-selects>          option controls -> variant -> the page
     <media-gallery>            the media viewer
     <sticky-atc>               the docked add bar
     <product-recommendations>  lazy loaded "you might also like"

   Loaded with `defer`, after theme.js (which defines window.ZUtils) and
   animations.js (window.ZAnim) and cart.js (<product-form>). Neither of
   those is assumed to exist: every call into them is guarded, so a product
   page still selects variants and swaps images if one of them fails.

   House rules, same as the rest of the theme
   ------------------------------------------
   - A missing child never throws. Every element degrades to "does nothing".
   - Nothing is registered twice; the Theme Editor re-renders sections
     constantly and `customElements.define` throws on a repeat.
   - Every listener added is remembered and removed in disconnectedCallback.
   - Reduced motion is honoured: smooth scrolling becomes instant scrolling.

   How a variant change flows
   --------------------------
     1. A control changes.
     2. The matching variant is found in the JSON the section printed, and
        the page is updated immediately from it — price, add button state,
        SKU, the option availability marks, the gallery. No network wait.
     3. In parallel the Section Rendering API is asked for the same section
        at the new variant. Every `[data-variant-target]` in the section is
        refilled from the node with the same id in the response. That is the
        authoritative pass: it brings back anything the local JSON cannot
        know, such as the merchant's own money format, unit prices, the
        inventory sentence and the payment buttons.
     4. `history.replaceState` puts `?variant=` in the address bar so the
        page can be shared or reloaded on the chosen variant.
   ========================================================================== */

(function () {
  'use strict';

  var win = window;
  var doc = document;

  /* ======================================================================
     Helpers
     ====================================================================== */

  function qs(selector, scope) {
    if (!selector) return null;
    var root = scope || doc;
    if (!root || typeof root.querySelector !== 'function') return null;
    try {
      return root.querySelector(selector);
    } catch (error) {
      return null;
    }
  }

  function qsa(selector, scope) {
    if (!selector) return [];
    var root = scope || doc;
    if (!root || typeof root.querySelectorAll !== 'function') return [];
    try {
      return Array.prototype.slice.call(root.querySelectorAll(selector));
    } catch (error) {
      return [];
    }
  }

  function isFn(value) {
    return typeof value === 'function';
  }

  function utils() {
    return win.ZUtils || {};
  }

  function reducedMotion() {
    var helpers = utils();
    if (isFn(helpers.prefersReducedMotion)) return helpers.prefersReducedMotion();
    try {
      return win.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (error) {
      return false;
    }
  }

  function scrollMode() {
    return reducedMotion() ? 'auto' : 'smooth';
  }

  function formatMoney(cents) {
    var helpers = utils();
    if (isFn(helpers.formatMoney)) return helpers.formatMoney(cents);
    var amount = Number(cents);
    if (!isFinite(amount)) amount = 0;
    return (amount / 100).toFixed(2);
  }

  /** One string out of the window.Zyrex bootstrap, with a safe fallback. */
  function string(group, key, fallback) {
    var root = win.Zyrex || {};
    var strings = root.strings || {};
    var bucket = strings[group] || {};
    var value = bucket[key];
    if (value === undefined || value === null || value === '') return fallback || '';
    return String(value);
  }

  function setText(node, value) {
    if (!node) return;
    node.textContent = value === undefined || value === null ? '' : String(value);
  }

  /**
   * Rewrite the money inside a price node without losing the visually hidden
   * "regular price" / "sale price" label that sits in front of it.
   */
  function setMoneyText(node, cents) {
    if (!node) return;
    var label = node.querySelector ? node.querySelector('.visually-hidden') : null;
    node.textContent = '';
    if (label) node.appendChild(label);
    node.appendChild(doc.createTextNode(' ' + formatMoney(cents)));
  }

  function clamp(value, min, max) {
    if (value < min) return min;
    if (value > max) return max;
    return value;
  }

  function emit(name, detail) {
    var event;
    try {
      event = new CustomEvent(name, { bubbles: true, detail: detail || {} });
    } catch (error) {
      return;
    }
    doc.dispatchEvent(event);
  }

  /** Re-run whatever the freshly injected markup needs to come alive. */
  function reinit(scope) {
    var anim = win.ZAnim;
    if (anim && isFn(anim.refresh)) {
      try {
        anim.refresh(scope || doc);
      } catch (error) {
        /* animations are optional */
      }
    }

    var shopify = win.Shopify;
    if (shopify && shopify.PaymentButton && isFn(shopify.PaymentButton.init)) {
      try {
        shopify.PaymentButton.init();
      } catch (error) {
        /* the wallet buttons redraw themselves on the next paint */
      }
    }
  }

  function sectionRequestUrl(sectionId, productUrl, variantId) {
    var helpers = utils();
    if (isFn(helpers.sectionUrl)) {
      return helpers.sectionUrl(sectionId, {
        url: productUrl,
        clean: true,
        params: { variant: variantId }
      });
    }

    var base = String(productUrl || win.location.pathname).split('?')[0];
    return (
      base +
      '?section_id=' +
      encodeURIComponent(sectionId) +
      '&variant=' +
      encodeURIComponent(variantId)
    );
  }

  function parseHTML(text) {
    try {
      return new DOMParser().parseFromString(text, 'text/html');
    } catch (error) {
      return null;
    }
  }

  function define(name, constructor) {
    if (!win.customElements || !isFn(win.customElements.define)) return;
    if (win.customElements.get(name)) return;
    try {
      win.customElements.define(name, constructor);
    } catch (error) {
      /* another script won the race — its definition is just as good */
    }
  }

  /* ======================================================================
     A base element that cleans up after itself
     ====================================================================== */

  class ZProductElement extends HTMLElement {
    constructor() {
      super();
      this.bindings = [];
      this.timers = [];
    }

    listen(target, type, handler, options) {
      if (!target || !isFn(target.addEventListener) || !isFn(handler)) return handler;
      target.addEventListener(type, handler, options);
      this.bindings.push({ target: target, type: type, handler: handler, options: options });
      return handler;
    }

    unlisten(target, type, handler) {
      if (!target || !isFn(target.removeEventListener)) return;
      target.removeEventListener(type, handler);
      this.bindings = this.bindings.filter(function (entry) {
        return !(entry.target === target && entry.type === type && entry.handler === handler);
      });
    }

    timer(id) {
      this.timers.push(id);
      return id;
    }

    teardown() {
      this.bindings.forEach(function (entry) {
        try {
          entry.target.removeEventListener(entry.type, entry.handler, entry.options);
        } catch (error) {
          /* the node is already gone */
        }
      });
      this.bindings = [];

      this.timers.forEach(function (id) {
        win.clearTimeout(id);
      });
      this.timers = [];
    }
  }

  /* ======================================================================
     <variant-selects>

     Markup contract (sections/main-product.liquid)
       root                    [data-section-id] [data-url] [data-option-count]
                               [data-update-url]
       variant data            script[type="application/json"][data-variant-json]
       one control per option  [data-variant-option] [data-option-position]
                               radios carry [data-option-value]
       chosen value readout    [data-option-selected] inside the legend
       no-JS carrier           select[data-variant-fallback] named `id`,
                               linked to the buy form with `form=`
       live region             [data-variant-status]
     ====================================================================== */

  class VariantSelects extends ZProductElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      this.sectionId = this.getAttribute('data-section-id') || '';
      this.productId = this.getAttribute('data-product-id') || '';
      this.productUrl = this.getAttribute('data-url') || win.location.pathname;
      this.updateAddressBar = this.getAttribute('data-update-url') !== 'false';
      this.optionCount = parseInt(this.getAttribute('data-option-count'), 10) || 0;

      this.section =
        (isFn(this.closest) && this.closest('[data-section-type="main-product"]')) ||
        doc.getElementById('shopify-section-' + this.sectionId) ||
        doc.body;

      this.variants = this.readVariants();
      this.fallback = qs('[data-variant-fallback]', this);
      this.status = qs('[data-variant-status]', this);

      if (this.optionCount < 1) {
        this.optionCount = this.countOptions();
      }

      /* The fallback select was the control until this line ran. From here
         the pickers above it are, so it stops being announced and stops
         being tabbable — but it stays in the DOM as the one element named
         `id`, which is what the buy form actually submits. */
      var wrap = qs('[data-variant-fallback-wrap]', this);
      if (wrap) wrap.setAttribute('data-ready', 'true');
      if (this.fallback) {
        this.fallback.setAttribute('aria-hidden', 'true');
        this.fallback.setAttribute('tabindex', '-1');
      }

      this.onChange = this.onChange.bind(this);
      this.listen(this, 'change', this.onChange);

      var values = this.selectedValues();
      this.currentVariant = this.findVariant(values);
      this.markAvailability(values);
      this.syncVariantId(this.currentVariant);
    }

    disconnectedCallback() {
      this.abort();
      this.teardown();
    }

    /* ---- Reading the section's own data ------------------------------- */

    readVariants() {
      var helpers = utils();
      var node = qs('script[type="application/json"][data-variant-json]', this);
      if (!node) return [];

      var parsed = null;
      if (isFn(helpers.parseJSONScript)) {
        parsed = helpers.parseJSONScript(node);
      } else {
        try {
          parsed = JSON.parse(node.textContent);
        } catch (error) {
          parsed = null;
        }
      }

      if (!parsed || !parsed.length) return [];
      return parsed;
    }

    countOptions() {
      var highest = 0;
      qsa('[data-option-position]', this).forEach(function (node) {
        var position = parseInt(node.getAttribute('data-option-position'), 10) || 0;
        if (position > highest) highest = position;
      });
      return highest;
    }

    /* ---- Selection ---------------------------------------------------- */

    controlFor(position) {
      var select = qs('select[data-option-position="' + position + '"]', this);
      if (select) return select;
      return qs('input[type="radio"][data-option-position="' + position + '"]:checked', this);
    }

    nodesFor(position) {
      var select = qs('select[data-option-position="' + position + '"]', this);
      if (select) return qsa('option', select);
      return qsa('input[type="radio"][data-option-position="' + position + '"]', this);
    }

    selectedValues() {
      var values = [];
      for (var position = 1; position <= this.optionCount; position++) {
        var control = this.controlFor(position);
        values.push(control ? control.value : null);
      }
      return values;
    }

    matches(variant, values) {
      if (!variant) return false;
      var options = variant.options || [];
      for (var i = 0; i < values.length; i++) {
        var wanted = values[i];
        if (wanted === null || wanted === undefined || wanted === '') continue;
        if (String(options[i]) !== String(wanted)) return false;
      }
      return true;
    }

    findVariant(values) {
      for (var i = 0; i < this.variants.length; i++) {
        if (this.matches(this.variants[i], values)) return this.variants[i];
      }
      return null;
    }

    hasAvailable(values) {
      for (var i = 0; i < this.variants.length; i++) {
        var variant = this.variants[i];
        if (!variant || !variant.available) continue;
        if (this.matches(variant, values)) return true;
      }
      return false;
    }

    /* ---- The change itself -------------------------------------------- */

    onChange(event) {
      var target = event && event.target;
      if (!target || !target.hasAttribute || !target.hasAttribute('data-variant-option')) return;

      var values = this.selectedValues();
      var variant = this.findVariant(values);
      this.currentVariant = variant;

      this.paintChosenLabels();
      this.markAvailability(values);
      this.syncVariantId(variant);
      this.applyLocally(variant);
      this.announceVariant(variant);
      this.updateAddress(variant);
      this.notifyMedia(variant);
      this.refresh(variant);
    }

    /** The value printed next to each option name in the legend. */
    paintChosenLabels() {
      var self = this;
      qsa('fieldset[data-option-position]', this).forEach(function (group) {
        var position = parseInt(group.getAttribute('data-option-position'), 10) || 0;
        var readout = qs('[data-option-selected]', group);
        if (!readout) return;
        var control = self.controlFor(position);
        setText(readout, control ? control.value : '');
      });
    }

    /**
     * Grey out the combinations that cannot be bought. Nothing is disabled —
     * a shopper is allowed to land on a sold out combination and see it say
     * so, which is far less confusing than a value that refuses to click.
     */
    markAvailability(values) {
      for (var index = 0; index < this.optionCount; index++) {
        this.markOption(index, values);
      }
    }

    markOption(index, values) {
      var self = this;
      var nodes = this.nodesFor(index + 1);

      nodes.forEach(function (node) {
        var probe = values.slice();
        probe[index] = node.value;
        var available = self.hasAvailable(probe);

        if (node.tagName === 'OPTION') {
          if (available) node.removeAttribute('data-unavailable');
          else node.setAttribute('data-unavailable', 'true');
          return;
        }

        if (available) node.removeAttribute('aria-disabled');
        else node.setAttribute('aria-disabled', 'true');

        var wrapper = node.closest ? node.closest('.vpick__opt') : node.parentElement;
        if (wrapper && wrapper.classList) {
          wrapper.classList.toggle('is-unavailable', !available);
        }
      });
    }

    /** Every control on the page that has to carry the variant id. */
    syncVariantId(variant) {
      var id = variant && variant.id ? String(variant.id) : '';

      if (this.fallback) {
        this.fallback.value = id;
        if (this.fallback.value !== id) {
          /* The option is missing (a variant was deleted mid-session). Leave
             whatever the browser kept rather than submitting an empty id. */
        }
      }

      qsa('[data-variant-id-input], [data-sticky-variant-id]', this.section).forEach(function (input) {
        if (!id) return;
        input.value = id;
      });
    }

    /* ---- The instant pass --------------------------------------------- */

    applyLocally(variant) {
      /* getElementById, not querySelector: a section id from a JSON template
         reads like `template--1234__main`, which is a perfectly good id but
         an awkward CSS selector. */
      this.paintPrice(doc.getElementById('Price-' + this.sectionId), variant);

      var stickyBody = doc.getElementById('StickyAtcBody-' + this.sectionId);
      this.paintPrice(qs('.satc__price', stickyBody), variant);

      this.paintButtons(variant);
      this.paintSku(variant);
      this.paintStickySummary(variant);
    }

    paintPrice(root, variant) {
      if (!root || !variant) return;

      var box = qs('.price', root);
      var current = qs('.price__current', root);
      var compare = qs('.price__compare', root);
      var onSale =
        typeof variant.compare_at_price === 'number' && variant.compare_at_price > variant.price;

      setMoneyText(current, variant.price);

      if (compare) {
        if (onSale) {
          setMoneyText(compare, variant.compare_at_price);
          compare.hidden = false;
        } else {
          compare.hidden = true;
        }
      }

      if (box && box.classList) {
        box.classList.toggle('price--on-sale', !!onSale);
        box.classList.toggle('price--sold-out', variant.available === false);
      }
    }

    paintButtons(variant) {
      var soldOut = string('product', 'soldOut', 'Sold out');
      var unavailable = string('product', 'unavailable', 'Unavailable');
      var fallbackAdd = string('product', 'addToCart', 'Add to cart');

      qsa('[data-add-button]', this.section).forEach(function (button) {
        var label = qs('[data-add-label]', button) || qs('.btn__label', button);
        var preferred = button.getAttribute('data-add-label-default') || fallbackAdd;

        if (!variant) {
          button.disabled = true;
          setText(label, unavailable);
          return;
        }

        if (variant.available === false) {
          button.disabled = true;
          setText(label, soldOut);
          return;
        }

        button.disabled = false;
        setText(label, preferred);
      });
    }

    paintSku(variant) {
      var slot = qs('[data-sku-value]', this.section);
      if (!slot || !variant) return;
      setText(slot, variant.sku || '');
    }

    paintStickySummary(variant) {
      var slot = qs('[data-satc-variant]', this.section);
      if (!slot || !variant) return;
      setText(slot, variant.title || '');
    }

    announceVariant(variant) {
      if (!this.status) return;

      if (!variant) {
        setText(this.status, string('product', 'unavailable', 'Unavailable'));
        return;
      }

      var state = variant.available
        ? string('product', 'inventoryInStock', '')
        : string('product', 'soldOut', '');
      var parts = [variant.title, formatMoney(variant.price)];
      if (state) parts.push(state);
      setText(this.status, parts.join(' · '));
    }

    updateAddress(variant) {
      if (!this.updateAddressBar || !variant) return;
      if (!win.history || !isFn(win.history.replaceState)) return;

      try {
        var url = new URL(win.location.href);
        url.searchParams.set('variant', String(variant.id));
        win.history.replaceState({ variantId: variant.id }, '', url.toString());
      } catch (error) {
        /* an opaque or unusual URL — the page still works */
      }
    }

    notifyMedia(variant) {
      emit('zyrex:variant:change', {
        sectionId: this.sectionId,
        productId: this.productId,
        variant: variant
      });
    }

    /* ---- The authoritative pass ---------------------------------------- */

    abort() {
      if (this.controller && isFn(this.controller.abort)) {
        try {
          this.controller.abort();
        } catch (error) {
          /* already finished */
        }
      }
      this.controller = null;
    }

    refresh(variant) {
      if (!variant || !win.fetch) return;

      this.abort();

      var controller = null;
      try {
        controller = new AbortController();
      } catch (error) {
        controller = null;
      }
      this.controller = controller;

      var self = this;
      var url = sectionRequestUrl(this.sectionId, this.productUrl, variant.id);

      if (this.section && isFn(this.section.setAttribute)) {
        this.section.setAttribute('data-loading', 'true');
      }

      win
        .fetch(url, controller ? { signal: controller.signal } : undefined)
        .then(function (response) {
          if (!response || !response.ok) throw new Error('Section request failed');
          return response.text();
        })
        .then(function (html) {
          self.applyHTML(html);
        })
        .catch(function () {
          /* The instant pass already left the page correct enough to buy
             from. A failed refresh is a missed polish, not a broken page. */
        })
        .then(function () {
          if (self.section && isFn(self.section.removeAttribute)) {
            self.section.removeAttribute('data-loading');
          }
          self.controller = null;
        });
    }

    applyHTML(html) {
      var parsed = parseHTML(html);
      if (!parsed) return;

      qsa('[data-variant-target]', this.section).forEach(function (node) {
        if (!node.id) return;
        var incoming = parsed.getElementById(node.id);
        if (!incoming) return;
        if (node.innerHTML === incoming.innerHTML) return;
        node.innerHTML = incoming.innerHTML;
      });

      reinit(this.section);
      emit('zyrex:product:refreshed', { sectionId: this.sectionId });
    }
  }

  /* ======================================================================
     <media-gallery>

     Markup contract (snippets/media-gallery.liquid)
       root      [data-layout] [data-media-count] [data-product-id]
       track     [data-gallery-track]
       item      .gallery__item [data-media-id] [data-media-type]
                 [data-variant-ids] [data-index]
       thumb     [data-gallery-thumb] [data-media-id] [data-index]
       nav       [data-gallery-prev] [data-gallery-next]
       zoom      [data-gallery-zoom]

     The wrapper the section puts around it carries [data-gallery-filter] and
     [data-gallery-zoom-enabled].
     ====================================================================== */

  class MediaGallery extends ZProductElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      this.layout = this.getAttribute('data-layout') || 'carousel';
      this.productId = this.getAttribute('data-product-id') || '';
      this.track = qs('[data-gallery-track]', this);
      this.rail = qs('[data-gallery-rail]', this);
      this.items = qsa('.gallery__item', this);
      this.thumbs = qsa('[data-gallery-thumb]', this);
      this.prevButton = qs('[data-gallery-prev]', this);
      this.nextButton = qs('[data-gallery-next]', this);

      var host = isFn(this.closest) ? this.closest('[data-gallery-filter]') : null;
      this.filterMedia = !!host && host.getAttribute('data-gallery-filter') === 'true';
      this.zoomEnabled = !host || host.getAttribute('data-gallery-zoom-enabled') !== 'false';

      if (!this.items.length) return;

      this.index = 0;
      for (var i = 0; i < this.items.length; i++) {
        if (this.items[i].classList.contains('is-active')) {
          this.index = i;
          break;
        }
      }

      this.bindThumbs();
      this.bindNav();
      this.bindTrack();
      this.bindZoom();
      this.bindSwipe();

      this.onVariantChange = this.onVariantChange.bind(this);
      this.listen(doc, 'zyrex:variant:change', this.onVariantChange);

      this.paint(this.index);
    }

    disconnectedCallback() {
      this.unzoom();
      this.teardown();
    }

    /* ---- Wiring -------------------------------------------------------- */

    bindThumbs() {
      var self = this;
      if (!this.thumbs.length) return;

      this.thumbs.forEach(function (thumb, index) {
        thumb.setAttribute('tabindex', index === self.index ? '0' : '-1');

        self.listen(thumb, 'click', function (event) {
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;
          event.preventDefault();
          self.select(index, { user: true });
        });
      });

      if (this.rail) {
        this.listen(this.rail, 'keydown', function (event) {
          self.onRailKeydown(event);
        });
      }
    }

    onRailKeydown(event) {
      var key = event.key;
      var last = this.thumbs.length - 1;
      var next = null;

      if (key === 'ArrowRight' || key === 'ArrowDown') next = Math.min(last, this.index + 1);
      else if (key === 'ArrowLeft' || key === 'ArrowUp') next = Math.max(0, this.index - 1);
      else if (key === 'Home') next = 0;
      else if (key === 'End') next = last;
      else return;

      event.preventDefault();
      this.select(next, { user: true });

      var thumb = this.thumbs[next];
      if (thumb && isFn(thumb.focus)) {
        try {
          thumb.focus({ preventScroll: true });
        } catch (error) {
          thumb.focus();
        }
      }
    }

    bindNav() {
      var self = this;

      if (this.prevButton) {
        this.listen(this.prevButton, 'click', function (event) {
          event.preventDefault();
          self.step(-1);
        });
      }

      if (this.nextButton) {
        this.listen(this.nextButton, 'click', function (event) {
          event.preventDefault();
          self.step(1);
        });
      }
    }

    bindTrack() {
      if (!this.track || this.layout !== 'carousel') return;

      var self = this;
      var helpers = utils();
      var handler = function () {
        self.syncFromScroll();
      };

      this.listen(this.track, 'scroll', isFn(helpers.rafThrottle) ? helpers.rafThrottle(handler) : handler, {
        passive: true
      });
    }

    bindZoom() {
      var self = this;

      /* With JavaScript off the control is a link to the full size file and
         that is exactly right. Now that it toggles an in-place zoom instead,
         it says so: a toggle button that reports its own state. */
      if (this.zoomEnabled) {
        qsa('[data-gallery-zoom]', this).forEach(function (link) {
          link.setAttribute('role', 'button');
          link.setAttribute('aria-pressed', 'false');
        });
      }

      this.listen(this, 'click', function (event) {
        var link = event.target && event.target.closest ? event.target.closest('[data-gallery-zoom]') : null;
        if (!link || !self.contains(link)) return;
        if (!self.zoomEnabled) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;

        event.preventDefault();
        if (self.classList.contains('is-zoomed')) self.unzoom();
        else self.zoom(link);
      });
    }

    /**
     * Swipe is only wired when the track cannot scroll on its own. The
     * carousel snaps natively and a second handler on top of that fights
     * with the browser.
     */
    bindSwipe() {
      var self = this;
      var startX = 0;
      var startY = 0;
      var tracking = false;

      this.listen(
        this,
        'touchstart',
        function (event) {
          if (self.nativelyScrollable()) return;
          if (!event.touches || event.touches.length !== 1) return;
          tracking = true;
          startX = event.touches[0].clientX;
          startY = event.touches[0].clientY;
        },
        { passive: true }
      );

      this.listen(
        this,
        'touchend',
        function (event) {
          if (!tracking) return;
          tracking = false;
          if (!event.changedTouches || !event.changedTouches.length) return;

          var deltaX = event.changedTouches[0].clientX - startX;
          var deltaY = event.changedTouches[0].clientY - startY;
          if (Math.abs(deltaX) < 45 || Math.abs(deltaX) < Math.abs(deltaY)) return;

          self.step(deltaX < 0 ? 1 : -1);
        },
        { passive: true }
      );
    }

    nativelyScrollable() {
      if (!this.track) return false;
      return this.track.scrollWidth > this.track.clientWidth + 4;
    }

    /* ---- Selection ----------------------------------------------------- */

    visibleIndexes() {
      var list = [];
      this.items.forEach(function (item, index) {
        if (!item.hasAttribute('hidden')) list.push(index);
      });
      return list.length ? list : this.items.map(function (item, index) {
        return index;
      });
    }

    step(direction) {
      var order = this.visibleIndexes();
      var at = order.indexOf(this.index);
      if (at === -1) at = 0;
      var next = clamp(at + direction, 0, order.length - 1);
      this.select(order[next], { user: true });
    }

    /** Classes, thumbs, media pausing and nav state — no scrolling. */
    paint(index) {
      if (index < 0 || index >= this.items.length) return;
      this.index = index;

      this.items.forEach(function (item, i) {
        item.classList.toggle('is-active', i === index);
      });

      this.thumbs.forEach(function (thumb, i) {
        var active = i === index;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('tabindex', active ? '0' : '-1');
        if (active) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });

      this.pauseInactive();
      this.updateNav();
    }

    select(index, options) {
      var opts = options || {};
      if (index < 0 || index >= this.items.length) return;

      var changed = index !== this.index;
      this.paint(index);
      if (changed) this.unzoom();

      this.scrollToItem(this.items[index], opts.user === true);
      this.scrollThumbIntoView(index);
    }

    scrollToItem(item, userDriven) {
      if (!item) return;

      if (this.layout === 'carousel' && this.track) {
        var left = item.offsetLeft - this.track.offsetLeft;
        try {
          this.track.scrollTo({ left: left, behavior: scrollMode() });
        } catch (error) {
          this.track.scrollLeft = left;
        }
        return;
      }

      /* A stacked or grid gallery moves the whole page, so it only ever
         does that when a person asked for it. */
      if (!userDriven) return;
      try {
        item.scrollIntoView({ behavior: scrollMode(), block: 'nearest' });
      } catch (error) {
        /* older engines simply do not move */
      }
    }

    scrollThumbIntoView(index) {
      var thumb = this.thumbs[index];
      if (!thumb || !this.rail) return;

      var railBox = this.rail.getBoundingClientRect();
      var thumbBox = thumb.getBoundingClientRect();
      if (thumbBox.left >= railBox.left && thumbBox.right <= railBox.right) return;

      var left = thumb.offsetLeft - (this.rail.clientWidth - thumb.offsetWidth) / 2;
      try {
        this.rail.scrollTo({ left: left, behavior: scrollMode() });
      } catch (error) {
        this.rail.scrollLeft = left;
      }
    }

    syncFromScroll() {
      if (!this.track) return;

      var centre = this.track.scrollLeft + this.track.clientWidth / 2;
      var best = this.index;
      var bestDistance = Infinity;
      var trackLeft = this.track.offsetLeft;

      this.items.forEach(function (item, index) {
        if (item.hasAttribute('hidden')) return;
        var middle = item.offsetLeft - trackLeft + item.offsetWidth / 2;
        var distance = Math.abs(middle - centre);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = index;
        }
      });

      if (best !== this.index) this.paint(best);
    }

    updateNav() {
      var order = this.visibleIndexes();
      var at = order.indexOf(this.index);
      var first = at <= 0;
      var last = at === -1 || at >= order.length - 1;

      if (this.prevButton) {
        this.prevButton.disabled = first;
        this.prevButton.setAttribute('aria-disabled', first ? 'true' : 'false');
      }

      if (this.nextButton) {
        this.nextButton.disabled = last;
        this.nextButton.setAttribute('aria-disabled', last ? 'true' : 'false');
      }
    }

    /* ---- Video and models ---------------------------------------------- */

    pauseInactive() {
      var index = this.index;
      this.items.forEach(function (item, i) {
        if (i === index) return;
        qsa('video', item).forEach(function (video) {
          if (isFn(video.pause) && !video.paused) {
            try {
              video.pause();
            } catch (error) {
              /* an unplayable source */
            }
          }
        });
        qsa('model-viewer', item).forEach(function (viewer) {
          if (isFn(viewer.pause)) {
            try {
              viewer.pause();
            } catch (error) {
              /* the viewer script has not loaded yet */
            }
          }
        });
      });
    }

    /* ---- Zoom ----------------------------------------------------------- */

    zoom(link) {
      var item = this.items[this.index];
      var frame = item ? qs('.gallery__frame', item) : null;
      if (!frame) return;

      this.classList.add('is-zoomed');
      if (link) link.setAttribute('aria-pressed', 'true');

      this.zoomFrame = frame;
      this.style.setProperty('--zoom-x', '50%');
      this.style.setProperty('--zoom-y', '50%');

      var self = this;

      this.zoomMove = function (event) {
        var rect = frame.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        var x = clamp(((event.clientX - rect.left) / rect.width) * 100, 0, 100);
        var y = clamp(((event.clientY - rect.top) / rect.height) * 100, 0, 100);
        self.style.setProperty('--zoom-x', x + '%');
        self.style.setProperty('--zoom-y', y + '%');
      };

      this.zoomOut = function (event) {
        if (event.target && event.target.closest && event.target.closest('[data-gallery-zoom]')) return;
        self.unzoom();
      };

      this.zoomKey = function (event) {
        if (event.key === 'Escape') self.unzoom();
      };

      this.listen(frame, 'mousemove', this.zoomMove);
      this.listen(frame, 'click', this.zoomOut);
      this.listen(doc, 'keydown', this.zoomKey);
    }

    unzoom() {
      if (!this.classList.contains('is-zoomed')) return;

      this.classList.remove('is-zoomed');
      this.style.removeProperty('--zoom-x');
      this.style.removeProperty('--zoom-y');

      qsa('[data-gallery-zoom]', this).forEach(function (link) {
        link.setAttribute('aria-pressed', 'false');
      });

      if (this.zoomFrame) {
        this.unlisten(this.zoomFrame, 'mousemove', this.zoomMove);
        this.unlisten(this.zoomFrame, 'click', this.zoomOut);
      }
      this.unlisten(doc, 'keydown', this.zoomKey);

      this.zoomFrame = null;
      this.zoomMove = null;
      this.zoomOut = null;
      this.zoomKey = null;
    }

    /* ---- Variants -------------------------------------------------------- */

    onVariantChange(event) {
      var detail = (event && event.detail) || {};
      var variant = detail.variant;
      if (!variant) return;
      if (this.productId && detail.productId && String(detail.productId) !== String(this.productId)) {
        return;
      }

      if (this.filterMedia) this.applyFilter(variant.id);

      var mediaId = null;
      if (variant.featured_media && variant.featured_media.id) mediaId = variant.featured_media.id;

      if (mediaId === null || mediaId === undefined) {
        this.updateNav();
        return;
      }

      var target = -1;
      this.items.forEach(function (item, index) {
        if (String(item.getAttribute('data-media-id')) === String(mediaId)) target = index;
      });

      if (target >= 0) this.select(target, { user: false });
      else this.updateNav();
    }

    /**
     * Hide the media that belongs to a different variant. Media attached to
     * no variant at all is always kept — that is the packaging shot, the
     * scale reference, the video, and hiding it would be wrong.
     */
    applyFilter(variantId) {
      var wanted = String(variantId);
      var self = this;

      this.items.forEach(function (item, index) {
        var raw = (item.getAttribute('data-variant-ids') || '').trim();
        var owned = raw ? raw.split(/\s+/) : [];
        var hide = owned.length > 0 && owned.indexOf(wanted) === -1;

        if (hide) item.setAttribute('hidden', '');
        else item.removeAttribute('hidden');

        var thumb = self.thumbs[index];
        if (!thumb) return;
        var row = thumb.closest ? thumb.closest('.gallery__rail-item') : null;
        var node = row || thumb;
        if (hide) node.setAttribute('hidden', '');
        else node.removeAttribute('hidden');
      });

      /* If the active item just went away, move to the first one left. */
      var active = this.items[this.index];
      if (active && active.hasAttribute('hidden')) {
        var order = this.visibleIndexes();
        if (order.length) this.select(order[0], { user: false });
      }
    }
  }

  /* ======================================================================
     <sticky-atc>

     Watches the real buy button. Once it has scrolled off the top of the
     screen the docked bar slides up; the moment it comes back, the bar goes
     away again. It is `visibility: hidden` while docked away, so it is never
     in the tab order when it is not on screen.
     ====================================================================== */

  class StickyAtc extends ZProductElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      this.sectionId = this.getAttribute('data-section-id') || '';
      this.visible = false;
      this.hide();

      var selector = this.getAttribute('data-watch') || '';
      if (selector.charAt(0) === '#') {
        /* An id from a JSON template can contain `--` and `__`, so it is
           looked up by id rather than run through the selector engine. */
        this.target = doc.getElementById(selector.slice(1));
      } else if (selector) {
        this.target = qs(selector);
      } else {
        this.target = null;
      }

      if (!this.target || !('IntersectionObserver' in win)) return;

      var self = this;
      this.observer = new IntersectionObserver(
        function (entries) {
          self.onIntersect(entries);
        },
        { root: null, rootMargin: '0px 0px -35% 0px', threshold: 0 }
      );
      this.observer.observe(this.target);

      this.bindQuantity();
    }

    disconnectedCallback() {
      if (this.observer && isFn(this.observer.disconnect)) this.observer.disconnect();
      this.observer = null;
      this.teardown();
    }

    onIntersect(entries) {
      var entry = entries && entries[0];
      if (!entry) return;

      var scrolledPast = entry.boundingClientRect.top < 0;
      if (!entry.isIntersecting && scrolledPast) this.show();
      else this.hide();
    }

    show() {
      if (this.visible) return;
      this.visible = true;
      this.classList.add('is-visible');
      this.removeAttribute('aria-hidden');
      this.removeAttribute('inert');
    }

    hide() {
      if (this.visible === false && this.hasAttribute('aria-hidden')) return;
      this.visible = false;
      this.classList.remove('is-visible');
      this.setAttribute('aria-hidden', 'true');
      this.setAttribute('inert', '');
    }

    /** Keep the docked bar buying the same number of items as the page. */
    bindQuantity() {
      var source = doc.getElementById('Quantity-' + this.sectionId);
      if (!source) return;

      var self = this;
      var sync = function () {
        var slot = qs('[data-sticky-quantity]', self);
        if (!slot) return;
        var value = parseInt(source.value, 10);
        if (!isFinite(value) || value < 1) value = 1;
        slot.value = String(value);
      };

      this.listen(source, 'change', sync);
      this.listen(source, 'input', sync);
      sync();
    }
  }

  /* ======================================================================
     <product-recommendations>

     Empty on the first paint, with the URL to ask for on `data-url`. The
     request only goes out when the element is near the viewport, so a
     visitor who never scrolls that far never pays for it.
     ====================================================================== */

  class ProductRecommendations extends ZProductElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      this.url = this.getAttribute('data-url');
      if (!this.url || !win.fetch) return;

      /* Already answered server side (a second render, or the Theme Editor
         re-rendering the section) — nothing to fetch. */
      if (this.children.length > 0) return;

      if (!('IntersectionObserver' in win)) {
        this.load();
        return;
      }

      var self = this;
      this.observer = new IntersectionObserver(
        function (entries) {
          var near = entries.some(function (entry) {
            return entry.isIntersecting;
          });
          if (!near) return;
          if (self.observer) self.observer.disconnect();
          self.observer = null;
          self.load();
        },
        { rootMargin: '0px 0px 420px 0px', threshold: 0 }
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      if (this.observer && isFn(this.observer.disconnect)) this.observer.disconnect();
      this.observer = null;
      this.teardown();
    }

    load() {
      if (this.loading || this.loaded) return;
      this.loading = true;

      var self = this;

      win
        .fetch(this.url)
        .then(function (response) {
          if (!response || !response.ok) throw new Error('Recommendations request failed');
          return response.text();
        })
        .then(function (html) {
          var parsed = parseHTML(html);
          if (!parsed) return;

          var incoming = parsed.querySelector('product-recommendations');
          if (!incoming) return;

          self.innerHTML = incoming.innerHTML;
          self.loaded = true;
          reinit(self);
          emit('zyrex:recommendations:loaded', { element: self });
        })
        .catch(function () {
          /* Nothing to show is a perfectly good outcome here: the section
             collapses to zero height and the page reads as if it was never
             asked for. */
        })
        .then(function () {
          self.loading = false;
        });
    }
  }

  /* ======================================================================
     Registration
     ====================================================================== */

  define('variant-selects', VariantSelects);
  define('media-gallery', MediaGallery);
  define('sticky-atc', StickyAtc);
  define('product-recommendations', ProductRecommendations);

  /* ======================================================================
     Theme Editor
     A section that is re-rendered gets brand new elements, which upgrade on
     their own. What does not come back on its own is the animation scan and
     the wallet buttons, so those are nudged here.
     ====================================================================== */

  if (win.Shopify && win.Shopify.designMode) {
    doc.addEventListener('shopify:section:load', function (event) {
      reinit(event.target || doc);
    });

    doc.addEventListener('shopify:block:select', function (event) {
      var block = event.target;
      if (!block || !isFn(block.scrollIntoView)) return;
      try {
        block.scrollIntoView({ behavior: scrollMode(), block: 'center' });
      } catch (error) {
        /* older editors simply do not move */
      }
    });
  }
})();
